import type { ReactNode } from "react";

import { cn } from "@/lib/utils/cn";

type BadgeTone =
  | "neutral"
  | "success"
  | "warning"
  | "info"
  | "destructive"
  | "attention";

/**
 * Soft tint, matching border, saturated text — the same recipe as `StatusPill`
 * and the Django `.badge-*` family. Solid fills made a row of badges read as
 * unrelated objects shouting over each other; the tint keeps the hue coding and
 * lets the label do the talking.
 *
 * Text is `-ink`, not the tone token itself: on its own tint the tone dropped
 * `success` to 4.43:1 and `destructive` to 4.11:1 (3.41:1 in dark). Every pair
 * below is asserted in tokens.contrast.test.ts, in both themes.
 */
const TONE_CLASSES: Record<BadgeTone, string> = {
  neutral: "border-border bg-muted text-muted-foreground",
  success: "border-success/30 bg-success/10 text-success-ink",
  warning: "border-warning/30 bg-warning/10 text-warning-ink",
  info: "border-info/30 bg-info/10 text-info-ink",
  destructive: "border-destructive/30 bg-destructive/10 text-destructive-ink",
  // Same tint as the rest; the dot is what makes it findable. Mirrors Django's
  // `.badge-orange`. Use it for "awaiting review" and nothing else — a second
  // dotted tone and neither one reads as the exception.
  attention:
    "gap-1.5 border-attention/30 bg-attention/10 text-attention-ink " +
    "before:size-1.5 before:shrink-0 before:rounded-full before:bg-attention before:content-['']",
};

/** Small status/label pill. Tones map to the design tokens (all ≥4.5:1). */
export function Badge({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
        TONE_CLASSES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
